import moment from "moment";

/**
 * Provides logic for evaluating patient eligibility based on external API
 * results, including Same or Similar (SameSim) checks from services like
 * pVerify.
 *
 * Workflow for SameSim evaluation:
 * 1. Filters and sorts SameSim results by recency.
 * 2. Normalizes `stringResult` structure for consistent access.
 * 3. Groups results by requestId and evaluates the latest response per group.
 * 4. If any results are still pending, marks the overall status as "pending".
 * 5. Otherwise, determines eligibility based on denial rules and HCPCS codes.
 *
 */
export default class Eligibility {
  static context;

  static setContext(context) {
    Eligibility.context = context;
  }

  static normalizePolicyId(value) {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim().toUpperCase();
    return normalized.length > 0 ? normalized : null;
  }

  static getPolicyIdFromParsedEligibilityResult(parsedResult) {
    const subscriberMemberId =
      parsedResult?.DemographicInfo?.Subscriber?.Identification?.find(
        (id) => id?.Type === "Member ID",
      )?.Code;
    const miscMemberId = parsedResult?.MiscellaneousInfoSummary?.MemberID;

    return Eligibility.normalizePolicyId(
      subscriberMemberId ?? miscMemberId ?? null,
    );
  }

  static getSubscriberInfoFromParsedEligibilityResult(parsedResult) {
    const subscriber = parsedResult?.DemographicInfo?.Subscriber;

    if (!subscriber) {
      return null;
    }

    const fullName =
      subscriber.FullName ||
      subscriber.Fullname ||
      [subscriber.Firstname, subscriber.Middlename, subscriber.Lastname_R]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      null;

    const address1 = subscriber.Address1 || null;
    const address2 = subscriber.Address2 || null;
    const city = subscriber.City || null;
    const state = subscriber.State || null;
    const zip = String(subscriber.Zip ?? "").trim() || null;
    const dob = String(subscriber.DOB_R ?? "").trim() || null;

    return {
      name: fullName,
      address1,
      address2,
      city,
      state,
      zip,
      dob,
      gender: subscriber.Gender_R || null,
    };
  }

  /**
   * Selects a family value when directed by the individual benefit note,
   * otherwise using the DME summary, HBPC summary, or matching detail.
   * @param {Object} dmeBenefit - DME summary individual benefit.
   * @param {Object} healthBenefitPlanCoverageBenefit - HBPC summary individual benefit.
   * @param {Object} dmeFamilyBenefit - DME summary family benefit.
   * @param {Object} healthBenefitPlanCoverageFamilyBenefit - HBPC summary family benefit.
   * @param {Array<Object>} healthBenefitPlanCoverageDetails - Raw HBPC eligibility details.
   * @param {string} eligibilityOrBenefit - Benefit type to match.
   * @param {string} planNetworkIndicator - Network indicator to match.
   * @param {Array<string>} timePeriodQualifiers - Accepted time period qualifiers.
   * @param {string} coverageLevel - Coverage level to match.
   * @returns {number|null} The first non-zero summary value, matching detail value, or null.
   */
  static getBenefitValue(
    dmeBenefit,
    healthBenefitPlanCoverageBenefit,
    dmeFamilyBenefit,
    healthBenefitPlanCoverageFamilyBenefit,
    healthBenefitPlanCoverageDetails,
    eligibilityOrBenefit,
    planNetworkIndicator,
    timePeriodQualifiers,
    coverageLevel,
  ) {
    if (
      timePeriodQualifiers.length === 0 ||
      !planNetworkIndicator ||
      !eligibilityOrBenefit ||
      !coverageLevel
    ) {
      return null;
    }

    /**
     * Converts a formatted eligibility value to a number.
     * @param {*} value - Eligibility value to convert.
     * @returns {number|null} Parsed number, or null when no number is present.
     */
    const toNumber = (value) => {
      const cleanedValue =
        typeof value === "string" ? value.replace(/[^0-9.-]+/g, "") : value;
      const numberValue = parseFloat(cleanedValue);
      return Number.isNaN(numberValue) ? null : numberValue;
    };
    const familyAmountNotes = [
      "DEDUCTIBLE IS MET WHEN FAMILY AMOUNT IS SATISFIED",
      "OOP MAXIMUM IS MET WHEN FAMILY AMOUNT IS SATISFIED",
    ];

    if (
      familyAmountNotes.includes(dmeFamilyBenefit?.Notes?.trim().toUpperCase())
    ) {
      return toNumber(dmeFamilyBenefit?.Value);
    }

    if (
      familyAmountNotes.includes(
        healthBenefitPlanCoverageFamilyBenefit?.Notes?.trim().toUpperCase(),
      )
    ) {
      return toNumber(healthBenefitPlanCoverageFamilyBenefit?.Value);
    }

    const dmeValue = toNumber(dmeBenefit?.Value);
    if (dmeValue !== 0 && dmeValue !== null) {
      return dmeValue;
    }

    const healthBenefitPlanCoverageValue = toNumber(
      healthBenefitPlanCoverageBenefit?.Value,
    );
    if (
      healthBenefitPlanCoverageValue !== 0 &&
      healthBenefitPlanCoverageValue !== null
    ) {
      return healthBenefitPlanCoverageValue;
    }

    const matchingBenefitDetails = healthBenefitPlanCoverageDetails?.find(
      (benefitDetails) =>
        benefitDetails?.EligibilityOrBenefit === eligibilityOrBenefit &&
        benefitDetails?.PlanNetworkIndicator === planNetworkIndicator &&
        benefitDetails?.TimePeriodQualifier != null &&
        timePeriodQualifiers.includes(benefitDetails.TimePeriodQualifier) &&
        benefitDetails?.CoverageLevel === coverageLevel,
    );

    return toNumber(matchingBenefitDetails?.MonetaryAmount);
  }

  /**
   * Parses and normalizes eligibility API results for a given insurance type.
   * Handles Medicare, Medicaid, and commercial plans, extracting deductible,
   * out-of-pocket, and plan summary information.
   * @param {string} type - Insurance type ("primary"/"secondary").
   * @param {Object} order - Order object containing insurance details.
   * @param {*} eligibilityResult - Array containing the selected eligibility result.
   * @returns {Object} Normalized eligibility data or error description.
   */
  static getEligibilityData(type, order, eligibilityResult) {
    const isMedicaid =
      type.toLowerCase() === "primary"
        ? order.insuranceTypeId === 2
        : order.secondaryInsuranceTypeId === 2;

    if (
      !eligibilityResult ||
      !Array.isArray(eligibilityResult) ||
      eligibilityResult.length === 0
    ) {
      return {
        status: "unknown",
        eligible: null,
        detail: {
          insuranceType: type,
          errorDescription: "No eligibility data received",
        },
      };
    }

    let eligibilityData;
    try {
      eligibilityData = JSON.parse(eligibilityResult[0]?.stringResult);
    } catch (err) {
      return {
        status: "error",
        eligible: null,
        detail: {
          insuranceType: type,
          errorDescription: "Invalid eligibility data format",
        },
      };
    }

    if (
      eligibilityData?.message ||
      eligibilityData?.ErrorDescription !== null
    ) {
      return {
        status: "error",
        eligible: null,
        detail: {
          insuranceType: type,
          errorDescription:
            (eligibilityData?.message || eligibilityData?.ErrorDescription) +
            (eligibilityData?.PossibleResolution
              ? " " + eligibilityData?.PossibleResolution
              : ""),
        },
      };
    }

    const noteData = {
      DemographicInfo: eligibilityData?.DemographicInfo || null,
      PlanCoverageSummary: eligibilityData?.PlanCoverageSummary || null,
      OtherPayerInfo: eligibilityData?.OtherPayerInfo || null,
      MedicareInfoSummary: eligibilityData?.MedicareInfoSummary || null,
      PCPAuthInfoSummary: eligibilityData?.PCPAuthInfoSummary || null,
      MiscellaneousInfoSummary:
        eligibilityData?.MiscellaneousInfoSummary || null,
      HBPC_Deductible_OOP_Summary:
        eligibilityData?.HBPC_Deductible_OOP_Summary || null,
      DMESummary: eligibilityData?.DMESummary || null,
      AdditionalInfo:
        eligibilityData?.AddtionalInfo ?? eligibilityData?.AdditionalInfo ?? "",
      SubscriberInfo:
        Eligibility.getSubscriberInfoFromParsedEligibilityResult(
          eligibilityData,
        ),
      PlanBenefits: null,
    };

    if (eligibilityData?.MedicareInfoSummary) {
      const partAValue =
        eligibilityData.MedicareInfoSummary.Part_A_Deductible_Remaining?.Value;
      const partBValue =
        eligibilityData.MedicareInfoSummary.Part_B_Deductible_Remaining?.Value;

      const cleanPartA =
        typeof partAValue === "string"
          ? partAValue.replace(/[^0-9.-]+/g, "")
          : partAValue;
      const cleanPartB =
        typeof partBValue === "string"
          ? partBValue.replace(/[^0-9.-]+/g, "")
          : partBValue;

      let isProviderInNetwork = eligibilityData?.IsProviderInNetwork;
      const createDate = moment(
        eligibilityResult[0]?.createDate,
        "M/D/YYYY h:mm:ss A",
      );
      const cutoffDate = moment("11/18/2025 00:00:00", "M/D/YYYY HH:mm:ss");
      if (createDate.isBefore(cutoffDate)) {
        // Don't trust this flag until this date.
        isProviderInNetwork = null;
      }

      return {
        status: "processed",
        eligible:
          eligibilityData?.PlanCoverageSummary?.Status === "Active"
            ? true
            : false,
        detail: {
          createDate: moment(eligibilityResult[0]?.createDate).format(
            "M/D/YYYY",
          ),
          insuranceType: type,
          isMedicare: true,
          isMedicaid: false,
          inNetwork: true,
          isProviderInNetwork: isProviderInNetwork,
          isSkipped:
            typeof eligibilityData?.IsSkipped === "boolean"
              ? eligibilityData.IsSkipped
              : null,
          deductible: {
            partA: !isNaN(cleanPartA) ? parseFloat(cleanPartA) : null,
            partB: !isNaN(cleanPartB) ? parseFloat(cleanPartB) : null,
          },
          dmeSummary: {
            // Noted in the backend parser but Merigold Trinity Health payer is always 20% on coinsurance.
            // However pverify sends us 0.
            coInsuranceInNetwork:
              eligibilityData?.PverifyPayerCode === "000959"
                ? "20%"
                : eligibilityData.MedicareInfoSummary.MedicareCoInsurance
                    ?.Value,
            coInsuranceOutNetwork: "N/A",
          },
          pharmacyPayerName:
            eligibilityData.MedicareInfoSummary.PharmacyPayerName,
          planName: eligibilityData?.PlanCoverageSummary?.PlanName,
          policyType: eligibilityData?.PlanCoverageSummary?.PolicyType,
          noteData: noteData,
          pvPayerCode: eligibilityData?.PverifyPayerCode,
          policyId:
            eligibilityData?.DemographicInfo?.Subscriber?.Identification.find(
              (id) => id.Type === "Member ID",
            )?.Code,
          subscriberInfo: noteData.SubscriberInfo,
          outOfPocket: {
            inNetwork: null,
            outNetwork: null,
          },
          additionalInfo: Eligibility.parseAdditionalInfo(
            eligibilityData?.AddtionalInfo ??
              eligibilityData?.AdditionalInfo ??
              "",
          ),
          exceptionNotes: eligibilityData?.ExceptionNotes,
        },
      };
    }

    const healthBenefitPlanCoverageDetails =
      eligibilityData?.ServiceDetails?.find(
        (details) => details?.ServiceName === "Health Benefit Plan Coverage",
      )?.EligibilityDetails ?? [];

    // Year to date values
    const timePeriodQualifiers = ["Calendar Year", "Year to Date"];

    var individualDeductibleInNetwork = Eligibility.getBenefitValue(
      eligibilityData?.DMESummary?.IndividualDeductibleInNet,
      eligibilityData?.HBPC_Deductible_OOP_Summary?.IndividualDeductibleInNet,
      eligibilityData?.DMESummary?.FamilyDeductibleInNet,
      eligibilityData?.HBPC_Deductible_OOP_Summary?.FamilyDeductibleInNet,
      healthBenefitPlanCoverageDetails,
      "Deductible",
      "Yes",
      timePeriodQualifiers,
      "Individual",
    );

    var individualDeductibleOutNetwork = Eligibility.getBenefitValue(
      eligibilityData?.DMESummary?.IndividualDeductibleOutNet,
      eligibilityData?.HBPC_Deductible_OOP_Summary?.IndividualDeductibleOutNet,
      eligibilityData?.DMESummary?.FamilyDeductibleOutNet,
      eligibilityData?.HBPC_Deductible_OOP_Summary?.FamilyDeductibleOutNet,
      healthBenefitPlanCoverageDetails,
      "Deductible",
      "Both",
      timePeriodQualifiers,
      "Individual",
    );

    var individualOOPInNetwork = Eligibility.getBenefitValue(
      eligibilityData?.DMESummary?.IndividualOOP_InNet,
      eligibilityData?.HBPC_Deductible_OOP_Summary?.IndividualOOP_InNet,
      eligibilityData?.DMESummary?.FamilyOOP_InNet,
      eligibilityData?.HBPC_Deductible_OOP_Summary?.FamilyOOP_InNet,
      healthBenefitPlanCoverageDetails,
      "Out of Pocket",
      "Yes",
      timePeriodQualifiers,
      "Individual",
    );

    var individualOOPOutNetwork = Eligibility.getBenefitValue(
      eligibilityData?.DMESummary?.IndividualOOP_OutNet,
      eligibilityData?.HBPC_Deductible_OOP_Summary?.IndividualOOP_OutNet,
      eligibilityData?.DMESummary?.FamilyOOP_OutNet,
      eligibilityData?.HBPC_Deductible_OOP_Summary?.FamilyOOP_OutNet,
      healthBenefitPlanCoverageDetails,
      "Out of Pocket",
      "Both",
      timePeriodQualifiers,
      "Individual",
    );

    // Remaining Values
    const remainingTimePeriodQualifiers = ["Remaining"];
    const individualDeductibleRemainingInNetwork = isMedicaid
      ? 0
      : Eligibility.getBenefitValue(
          eligibilityData?.DMESummary?.IndividualDeductibleRemainingInNet,
          eligibilityData?.HBPC_Deductible_OOP_Summary
            ?.IndividualDeductibleRemainingInNet,
          eligibilityData?.DMESummary?.FamilyDeductibleRemainingInNet,
          eligibilityData?.HBPC_Deductible_OOP_Summary
            ?.FamilyDeductibleRemainingInNet,
          healthBenefitPlanCoverageDetails,
          "Deductible",
          "Yes",
          remainingTimePeriodQualifiers,
          "Individual",
        );
    const individualDeductibleRemainingOutNetwork = isMedicaid
      ? 0
      : Eligibility.getBenefitValue(
          eligibilityData?.DMESummary?.IndividualDeductibleRemainingOutNet,
          eligibilityData?.HBPC_Deductible_OOP_Summary
            ?.IndividualDeductibleRemainingOutNet,
          eligibilityData?.DMESummary?.FamilyDeductibleRemainingOutNet,
          eligibilityData?.HBPC_Deductible_OOP_Summary
            ?.FamilyDeductibleRemainingOutNet,
          healthBenefitPlanCoverageDetails,
          "Deductible",
          "Both",
          remainingTimePeriodQualifiers,
          "Individual",
        );

    const individualOOPRemainingInNetwork = isMedicaid
      ? 0
      : Eligibility.getBenefitValue(
          eligibilityData?.DMESummary?.IndividualOOPRemainingInNet,
          eligibilityData?.HBPC_Deductible_OOP_Summary
            ?.IndividualOOPRemainingInNet,
          eligibilityData?.DMESummary?.FamilyOOPRemainingInNet,
          eligibilityData?.HBPC_Deductible_OOP_Summary?.FamilyOOPRemainingInNet,
          healthBenefitPlanCoverageDetails,
          "Out of Pocket",
          "Yes",
          remainingTimePeriodQualifiers,
          "Individual",
        );

    const individualOOPRemainingOutNetwork = isMedicaid
      ? 0
      : Eligibility.getBenefitValue(
          eligibilityData?.DMESummary?.IndividualOOPRemainingOutNet,
          eligibilityData?.HBPC_Deductible_OOP_Summary
            ?.IndividualOOPRemainingOutNet,
          eligibilityData?.DMESummary?.FamilyOOPRemainingOutNet,
          eligibilityData?.HBPC_Deductible_OOP_Summary
            ?.FamilyOOPRemainingOutNet,
          healthBenefitPlanCoverageDetails,
          "Out of Pocket",
          "Both",
          remainingTimePeriodQualifiers,
          "Individual",
        );

    let isProviderInNetwork = eligibilityData?.IsProviderInNetwork;
    const createDate = moment(
      eligibilityResult[0]?.createDate,
      "M/D/YYYY h:mm:ss A",
    );
    const cutoffDate = moment("11/18/2025 00:00:00", "M/D/YYYY HH:mm:ss");
    if (createDate.isBefore(cutoffDate)) {
      // Don't trust this flag until this date.
      isProviderInNetwork = null;
    }

    const durableMedicalEquipmentPurchaseDetails =
      eligibilityData?.ServiceDetails?.find(
        (details) =>
          details?.ServiceName === "Durable Medical Equipment Purchase",
      )?.EligibilityDetails ?? [];
    const dmeInNetworkDetail = durableMedicalEquipmentPurchaseDetails.find(
      (details) =>
        details?.Message?.includes(
          "ILLNESS PHYSICIAN OFFICE DURABLE MEDICAL EQPMT SPE",
        ) &&
        !Number.isNaN(parseFloat(details?.Percent)) &&
        parseFloat(details?.Percent) !== 0,
    );
    const dmeSummaryInNetworkPercentage =
      parseFloat(eligibilityData?.DMESummary?.CoInsInNet?.Value) || 0;

    // Noted in the backend parser but Merigold Trinity Health payer is always 20% on coinsurance.
    // However pverify sends us 0.
    const dmeInNetworkPercentage =
      eligibilityData?.PverifyPayerCode === "000959"
        ? "20%"
        : dmeSummaryInNetworkPercentage === 0
          ? `${(parseFloat(dmeInNetworkDetail?.Percent) || 0) * 100}%`
          : eligibilityData?.DMESummary?.CoInsInNet?.Value;

    // mutate the note data.
    if (noteData?.DMESummary?.CoInsInNet) {
      noteData.DMESummary.CoInsInNet.Value = dmeInNetworkPercentage;
    }

    noteData.PlanBenefits = {};
    noteData.PlanBenefits.CoinsuranceInNet = dmeInNetworkPercentage;
    noteData.PlanBenefits.CoinsuranceOutNet = isMedicaid
      ? "0%"
      : eligibilityData?.DMESummary?.CoInsOutNet?.Value || "0%";
    noteData.PlanBenefits.IndividualDeductibleInNet =
      individualDeductibleInNetwork !== null
        ? individualDeductibleInNetwork.toFixed(2)
        : null;
    noteData.PlanBenefits.IndividualDeductibleOutNet =
      individualDeductibleOutNetwork !== null
        ? individualDeductibleOutNetwork.toFixed(2)
        : null;
    noteData.PlanBenefits.IndividualOOP_InNet =
      individualOOPInNetwork !== null
        ? individualOOPInNetwork.toFixed(2)
        : null;
    noteData.PlanBenefits.IndividualOOP_OutNet =
      individualOOPOutNetwork !== null
        ? individualOOPOutNetwork.toFixed(2)
        : null;
    noteData.PlanBenefits.IndividualDeductibleRemainingInNet =
      individualDeductibleRemainingInNetwork !== null
        ? individualDeductibleRemainingInNetwork.toFixed(2)
        : null;
    noteData.PlanBenefits.IndividualDeductibleRemainingOutNet =
      individualDeductibleRemainingOutNetwork !== null
        ? individualDeductibleRemainingOutNetwork.toFixed(2)
        : null;
    noteData.PlanBenefits.IndividualOOPRemainingInNet =
      individualOOPRemainingInNetwork !== null
        ? individualOOPRemainingInNetwork.toFixed(2)
        : null;
    noteData.PlanBenefits.IndividualOOPRemainingOutNet =
      individualOOPRemainingOutNetwork !== null
        ? individualOOPRemainingOutNetwork.toFixed(2)
        : null;

    return {
      status: "processed",
      eligible:
        eligibilityData?.PlanCoverageSummary?.Status === "Active"
          ? true
          : false,
      detail: {
        createDate: moment(eligibilityResult[0]?.createDate).format("M/D/YYYY"),
        insuranceType: type,
        isMedicare: false,
        isMedicaid: isMedicaid,
        isProviderInNetwork: isProviderInNetwork,
        isSkipped:
          typeof eligibilityData?.IsSkipped === "boolean"
            ? eligibilityData.IsSkipped
            : null,
        dmeSummary: {
          coInsuranceInNetwork: isMedicaid ? "0%" : dmeInNetworkPercentage,
          coInsuranceOutNetwork: isMedicaid
            ? "0%"
            : eligibilityData?.DMESummary?.CoInsOutNet?.Value,
        },
        deductible: {
          inNetwork: individualDeductibleRemainingInNetwork,
          outNetwork: individualDeductibleRemainingOutNetwork,
        },
        outOfPocket: {
          inNetwork: individualOOPRemainingInNetwork,
          outNetwork: individualOOPRemainingOutNetwork,
        },
        planName: eligibilityData?.PlanCoverageSummary?.PlanName,
        policyType: eligibilityData?.PlanCoverageSummary?.PolicyType,
        noteData: noteData,
        pvPayerCode: eligibilityData?.PverifyPayerCode,
        policyId: eligibilityData?.MiscellaneousInfoSummary?.MemberID,
        subscriberInfo: noteData.SubscriberInfo,
        additionalInfo: Eligibility.parseAdditionalInfo(
          eligibilityData?.AddtionalInfo ??
            eligibilityData?.AdditionalInfo ??
            "",
        ),
        exceptionNotes: eligibilityData?.ExceptionNotes,
        payerName: eligibilityData?.PayerName,
      },
    };
  }

  /**
   * Selects the most recent eligibility API result for the requested type.
   *
   * Selection behavior:
   * - `transient`: return the most recent result without any payor/policy checks.
   * - `primary`/`secondary`: first try payor+policy matching; if none found,
   *   fall back to payor-only matching.
   *
   * @param {"primary"|"secondary"|"transient"} type - Selection strategy.
   * @param {Object} order - Order containing insurance subtype/policy fields.
   * @param {Array<Object>} apiResults - Candidate API result rows.
   * @returns {Array<Object>} Single-item array containing the most recent match, or empty array.
   */
  static getLatestMatchingEligibilityResult(type, order, apiResults) {
    if (!Array.isArray(apiResults) || apiResults.length === 0) {
      return [];
    }

    if (type === "transient") {
      // Transient responses should always display exactly what just came back.
      return apiResults.slice(-1);
    }

    const insuranceSubTypeId =
      type === "primary"
        ? order.insuranceSubTypeId
        : order.secondaryInsuranceSubTypeId;
    const currentPolicyId = Eligibility.normalizePolicyId(
      type === "primary"
        ? order?.insurancePolicy
        : order?.secondaryInsurancePolicy,
    );
    const insuranceSubType =
      Eligibility.context().insuranceSubTypesIndexed[insuranceSubTypeId];

    // First pass: strict match on both payor and policy (plus rejected responses).
    const matchingPayorPolicyResults = apiResults.filter((result) => {
      const resultType = result?.resultType?.toLowerCase();
      if (resultType !== type) {
        return false;
      }

      let parsedResult;
      try {
        parsedResult = JSON.parse(result.stringResult);
      } catch (e) {
        console.error("Failed to parse eligibility result:", e);
        return false;
      }
      const parsedPolicyId =
        Eligibility.getPolicyIdFromParsedEligibilityResult(parsedResult);
      const hasMatchingPolicy =
        !currentPolicyId || parsedPolicyId === currentPolicyId;
      const hasMatchingPayer =
        parsedResult?.PverifyPayerCode === String(insuranceSubType?.pvPayorId);
      const isRejectedResponse = String(parsedResult?.APIResponseCode) === "1";

      return isRejectedResponse || (hasMatchingPolicy && hasMatchingPayer);
    });

    if (matchingPayorPolicyResults.length > 0) {
      return matchingPayorPolicyResults.slice(-1);
    }

    // Fallback: if no payor+policy matches, loosen to payor-only.
    const matchingPayorResults = apiResults.filter((result) => {
      const resultType = result?.resultType?.toLowerCase();
      if (resultType !== type) {
        return false;
      }

      let parsedResult;
      try {
        parsedResult = JSON.parse(result.stringResult);
      } catch (e) {
        console.error("Failed to parse eligibility result:", e);
        return false;
      }

      const hasMatchingPayer =
        parsedResult?.PverifyPayerCode === String(insuranceSubType?.pvPayorId);
      const isRejectedResponse = String(parsedResult?.APIResponseCode) === "1";
      return isRejectedResponse || hasMatchingPayer;
    });

    return matchingPayorResults.slice(-1);
  }

  /**
   * Returns normalized eligibility data for insurance by type.
   *
   * Source priority:
   * 1. Most recent transient response (no matching checks)
   * 2. Most recent stored response using type-specific matching rules
   *
   * @param {"primary"|"secondary"} type - Insurance side being resolved.
   * @param {Object} order - Order object containing insurance details.
   * @param {Array<Object>} apiResults - Persisted API result objects.
   * @param {Array<Object>|null} transientApiResults - Newly returned in-memory results.
   * @returns {Object} Normalized eligibility data for the requested insurance side.
   */
  static getInsuranceEligibility(
    type,
    order,
    apiResults,
    transientApiResults = null,
  ) {
    const transientEligibilityResult =
      Eligibility.getLatestMatchingEligibilityResult(
        "transient",
        order,
        transientApiResults,
      );
    if (transientEligibilityResult.length > 0) {
      return this.getEligibilityData(type, order, transientEligibilityResult);
    }

    const storedEligibilityResult =
      Eligibility.getLatestMatchingEligibilityResult(type, order, apiResults);

    return this.getEligibilityData(type, order, storedEligibilityResult);
  }

  /**
   * Returns normalized eligibility data for primary insurance.
   * @param {*} apiResults - Array of API result objects.
   * @param {*} order - Order object containing insurance details.
   * @param {*} transientApiResults - Optional transient API result objects to prefer.
   * @returns {Object} Normalized eligibility data for primary insurance.
   */
  static getPrimary(apiResults, order, transientApiResults = null) {
    return this.getInsuranceEligibility(
      "primary",
      order,
      apiResults,
      transientApiResults,
    );
  }

  /**
   * Returns normalized eligibility data for secondary insurance.
   * @param {*} apiResults - Array of API result objects.
   * @param {*} order - Order object containing insurance details.
   * @param {*} transientApiResults - Optional transient API result objects to prefer.
   * @returns {Object} Normalized eligibility data for secondary insurance.
   */
  static getSecondary(apiResults, order, transientApiResults = null) {
    return this.getInsuranceEligibility(
      "secondary",
      order,
      apiResults,
      transientApiResults,
    );
  }

  /**
   * Returns normalized eligibility data for SNF.
   * @param {*} apiResults - Array of API result objects.
   * @returns {Object} Normalized eligibility data for SNF.
   */
  static getSNF(apiResults) {
    try {
      // Get the latest SNF result
      const snfResults = Array.isArray(apiResults)
        ? apiResults
            .filter((row) => row.resultType === "SNF")
            .sort((a, b) => b.id - a.id) // newest first to match the original logic
        : [];

      // If no SNF results, return null
      if (snfResults.length === 0) {
        return {
          eligible: null,
          status: null,
          detail: null,
        };
      } else {
        let eligibilityData;
        eligibilityData = JSON.parse(snfResults[0]?.stringResult);
        if (
          eligibilityData?.requestId === 0 ||
          eligibilityData?.APIResponseCode === 4
        ) {
          return {
            eligible: null,
            status: "error",
            detail: {
              errorMessage: eligibilityData.APIResponseMessage,
              createDate: snfResults[0]?.createDate,
            },
          };
        } else if (eligibilityData?.APIResponseCode === 3) {
          return {
            eligible: null,
            status: "pending",
            detail: {
              errorMessage: eligibilityData.APIResponseMessage,
              createDate: snfResults[0]?.createDate,
            },
          };
        } else if (eligibilityData?.APIResponseCode === 0) {
          return {
            eligible: null,
            status: "success",
            detail: {
              errorMessage: eligibilityData.APIResponseMessage,
              createDate: snfResults[0]?.createDate,
            },
          };
        } else {
          return {
            eligible: null,
            status: "error",
            detail: {
              errorMessage: eligibilityData.APIResponseMessage,
              createDate: snfResults[0]?.createDate,
            },
          };
        }
      }
    } catch (error) {
      return {
        eligible: null,
        status: "error",
        detail: { errorMessage: error.message },
      };
    }
  }

  /**
   * Evaluates Same or Similar (SameSim) API results to determine patient
   * eligibility. Handles grouping, normalization, pending/error/processed
   * status, and wheelchair claim checks.
   * @param {*} apiResults - Array of SameSim API result objects.
   * @returns {Object} Eligibility status, details, and warnings if
   * applicable.
   */
  static getSameSim(apiResults) {
    try {
      const sameSims = Array.isArray(apiResults)
        ? apiResults
            .filter((row) => row.resultType === "SameSim")
            .sort((a, b) => b.id - a.id) // newest first to match the original logic
        : [];

      if (sameSims.length === 0) {
        return {
          eligible: null,
          status: null,
          detail: null,
        };
      }

      const normalizedSameSims = sameSims
        .map((row) => {
          const stringResult = JSON.parse(row.stringResult);
          Eligibility.sameSimNormalizeStringResult(stringResult);
          return { ...row, stringResult };
        })
        .sort((a, b) => new Date(b.createDate) - new Date(a.createDate));

      const newestSameSim = normalizedSameSims[0];
      const newestSameSimStatus = Eligibility.sameSimDetermineOverallStatus(
        newestSameSim.stringResult,
      );

      //Don't check just latest for pending, but we do check just latest for error.
      if (newestSameSimStatus === "error") {
        return {
          eligible: null,
          status: "error",
          detail: {
            errorMessage:
              newestSameSim.stringResult.apiResponseMessage ??
              newestSameSim.stringResult.errorMessage ??
              "Unknown error",
          },
        };
      }

      const groupedByRequestId =
        Eligibility.sameSimGroupByRequestId(normalizedSameSims);
      const anyPending = groupedByRequestId.some(
        (row) =>
          Eligibility.sameSimDetermineOverallStatus(row.stringResult) ===
          "pending",
      );

      if (anyPending) {
        return Eligibility.sameSimHandlePendingResults(groupedByRequestId);
      }

      // newest processed per prefix
      const newestPerPrefix = new Map();
      for (const normalizedSameSim of normalizedSameSims) {
        const status = Eligibility.sameSimDetermineOverallStatus(
          normalizedSameSim.stringResult,
        );
        if (status !== "processed") {
          continue;
        }

        const prefix = Eligibility.sameSimGetHcpcsPrefix(
          normalizedSameSim.stringResult,
        );
        if (!newestPerPrefix.has(prefix)) {
          newestPerPrefix.set(prefix, normalizedSameSim);
        }
      }

      if (newestPerPrefix.size === 0) {
        return {
          eligible: null,
          status: "error",
          detail: {
            errorMessage:
              newestSameSim.stringResult.apiResponseMessage ??
              newestSameSim.stringResult.errorMessage ??
              "Unknown error",
          },
        };
      }

      const wheelchairClaimsInPast5Years =
        Eligibility.sameSimExtractWheelchairClaimsWithin5Years(newestPerPrefix);

      if (wheelchairClaimsInPast5Years.length > 0) {
        return {
          eligible: null,
          status: "warning",
          detail: { claims: wheelchairClaimsInPast5Years },
        };
      }

      return {
        eligible: true,
        status: "processed",
        detail: null,
      };
    } catch (error) {
      return {
        eligible: null,
        status: "error",
        detail: { errorMessage: error.message },
      };
    }
  }

  /**
   * Extracts wheelchair claims from SameSim results that occurred within the
   * past 5 years.
   * @param {*} sameSimResults - Iterable of SameSim result objects.
   * @returns {Array} Array of matching wheelchair claim objects.
   */
  static sameSimExtractWheelchairClaimsWithin5Years(sameSimResults) {
    const matches = [];

    for (const [, item] of sameSimResults.entries()) {
      const claims = item.stringResult?.claims || [];
      for (const claim of claims) {
        if (Eligibility.sameSimIsWheelchairClaim(claim)) {
          matches.push({
            hcpcsCode: claim.HCPCS,
            dosStart: claim.DOSStart,
          });
        }
      }
    }

    return matches;
  }

  /**
   * Determines if a claim is a wheelchair claim within the past 5 years.
   * @param {*} claim - Claim object to check.
   * @returns {boolean} True if claim is a wheelchair claim within 5 years,
   * else false.
   */
  static sameSimIsWheelchairClaim(claim) {
    if (!claim?.CategoryId || !claim?.DOSStart) return false;

    const isWheelchairCategory = claim.CategoryId === 18;

    const dos = new Date(claim.DOSStart);
    const isValidDate = !isNaN(dos.getTime());

    const fiveYearsAgo = new Date();
    fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);

    return isWheelchairCategory && isValidDate && dos >= fiveYearsAgo;
  }

  /**
   * Handles SameSim results that are still pending, calculating expected
   * completion time.
   * @param {*} groupedSameSimResults - Array of grouped SameSim results.
   * @returns {Object} Pending status and expected completion time details.
   */
  static sameSimHandlePendingResults(groupedSameSimResults) {
    const pendingWithTime = groupedSameSimResults.find(
      (row) =>
        Eligibility.sameSimDetermineOverallStatus(row.stringResult) ===
          "pending" &&
        row.stringResult?.expectedTimeInSeconds &&
        row.createDate,
    );

    let expectedTime = null;
    if (
      pendingWithTime?.stringResult?.expectedTimeInSeconds &&
      pendingWithTime?.createDate
    ) {
      // createDate is in Eastern time, so parse as local time (not UTC)
      const createdAtMoment = moment(
        pendingWithTime.createDate,
        "M/D/YYYY h:mm:ss A",
      );
      if (createdAtMoment.isValid()) {
        expectedTime = createdAtMoment
          .add(pendingWithTime.stringResult.expectedTimeInSeconds, "seconds")
          .toISOString(); // local time to ISO
      }
    }

    return {
      eligible: null,
      status: "pending",
      detail: {
        expectedTimeInSeconds:
          pendingWithTime?.stringResult?.expectedTimeInSeconds ?? null,
        expectedTimeUTC: expectedTime
          ? moment(expectedTime).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
          : null,
        expectedTimeLocal: expectedTime
          ? moment(expectedTime).format("YYYY-MM-DDTHH:mm:ss")
          : null,
      },
    };
  }

  /**
   * Normalizes the structure of a SameSim stringResult object for consistent
   * property access.
   * @param {*} stringResult - The stringResult object to normalize.
   * @returns {*} The normalized stringResult object.
   */
  static sameSimNormalizeStringResult(stringResult) {
    stringResult.requestId = stringResult.requestId ?? stringResult.RequestId;
    stringResult.processed = stringResult.processed ?? stringResult.Processed;
    stringResult.isDataError =
      stringResult.isDataError ?? stringResult.IsDataError;
    stringResult.errorMessage =
      stringResult.errorMessage ?? stringResult.ErrorMessage;
    stringResult.status = stringResult.status ?? stringResult.Status;
    stringResult.apiResponseCode =
      stringResult.apiResponseCode ?? stringResult.APIResponseCode;
    stringResult.apiResponseMessage =
      stringResult.apiResponseMessage ?? stringResult.APIResponseMessage;
    stringResult.detailsUrl =
      stringResult.detailsUrl ?? stringResult.DetailsURL;
    stringResult.message = stringResult.message ?? stringResult.Message;
    stringResult.claimsCount =
      stringResult.claimsCount ?? stringResult.ClaimsCount;
    stringResult.claims = stringResult.claims ?? stringResult.Claims;

    return stringResult;
  }

  /**
   * Groups SameSim results by requestId, returning the latest result for each
   * group. Ignores errors and results without a requestId.
   * @param {*} sameSimResults - Array of SameSim result objects.
   * @returns {Array} Array of latest SameSim results per requestId.
   */
  static sameSimGroupByRequestId(sameSimResults) {
    const latestByRequestId = new Map();

    for (const result of sameSimResults) {
      const requestId = result.stringResult?.requestId;
      if (!requestId || requestId === 0) continue;

      const existing = latestByRequestId.get(requestId);
      const currentId = result.id;

      if (!existing || currentId > existing.id) {
        latestByRequestId.set(requestId, result);
      }
    }

    return Array.from(latestByRequestId.values());
  }

  /**
   * Determines the overall status of a SameSim result based on its API
   * response code.
   * @param {*} stringResult - The stringResult object to check.
   * @returns {string} "pending", "processed", or "error".
   */
  static sameSimDetermineOverallStatus(stringResult) {
    // 0 Processed
    // 1 Rejected
    // 2 NoFunds
    // 3 Pending
    // 4 InvalidRequest
    // 5 InvalidFormat
    // 6 LocationError

    const code = Number(stringResult.apiResponseCode);
    if (code === 3) {
      return "pending";
    }
    if (code === 0) {
      return "processed";
    }
    return "error";
  }

  /**
   * Returns the HCPCS code prefix from the first claim in the SameSim result.
   * @param {*} body - The SameSim result object.
   * @returns {string} The HCPCS code prefix or "unknown".
   */
  static sameSimGetHcpcsPrefix(body) {
    if (!Array.isArray(body.claims) || body.claims.length === 0) {
      return "unknown";
    }

    const code = body.claims[0].hcpcsCode || "";
    return code.length > 0 ? code[0].toUpperCase() : "unknown";
  }

  /**
   * Checks if any claims in the SameSim result match denied HCPCS codes.
   * @param {*} body - The SameSim result object.
   * @param {*} denySet - Set of denied HCPCS codes.
   * @returns {Object} Object with hasDeniedMatch (boolean) and matchingCodes
   * (array).
   */
  static sameSimHasDeniedMatch(body, denySet) {
    const matchingCodes = [];
    let hasDeniedMatch = false;

    if (Array.isArray(body.claims)) {
      for (const claim of body.claims) {
        const code = (claim.hcpcsCode || "").toUpperCase();
        const status = (claim.status || "").toLowerCase();
        if (denySet.has(code) && status === "denied") {
          hasDeniedMatch = true;
          matchingCodes.push(code);
        }
      }
    }

    return { hasDeniedMatch, matchingCodes };
  }

  /**
   * Parses the additionalInfo string into an object with keys: "PAYER",
   * "VERIFICATION", "BILLING", each containing arrays of strings.
   * @param {string} additionalInfo
   * @returns {Object}
   */
  static parseAdditionalInfo(additionalInfo) {
    const result = {
      PAYER: [],
      VERIFICATION: [],
      BILLING: [],
    };

    if (!additionalInfo || typeof additionalInfo !== "string") {
      return result;
    }

    const additionalInfoCleaned = additionalInfo
      .replace(/\\r\\n/g, "\n")
      .trim();
    const regex = /\[(VERIFICATION|BILLING)\]/gi;
    let sections = [];
    let match;
    while ((match = regex.exec(additionalInfoCleaned)) !== null) {
      sections.push({ tag: match[1].toUpperCase(), start: match.index });
    }
    if (sections.length === 0) {
      result.PAYER.push(additionalInfoCleaned);
      return result;
    }

    // Payer section
    const firstTagStart = sections[0].start;
    const payerText = additionalInfoCleaned.substring(0, firstTagStart).trim();
    if (payerText) {
      result.PAYER.push(payerText);
    }

    // Verification/Billing sections
    for (let i = 0; i < sections.length; i++) {
      const tag = sections[i].tag;
      const start = sections[i].start + `[${tag}]`.length;
      const end =
        i + 1 < sections.length
          ? sections[i + 1].start
          : additionalInfoCleaned.length;
      const text = additionalInfoCleaned.substring(start, end).trim();
      if (text) {
        result[tag].push(text);
      }
    }

    return result;
  }
}
